import { routes } from '@/utils/routes';
import { GeneralErrorDialog } from '@rhg/components/registration/general-error-dialog';
import { FullWidthLinearProgress } from '@rhg/components/ui/progress';
import useAccount from '@rhg/hooks/use-account';
import { useNotificationUserConsentUpdate } from '@rhg/hooks/use-notification-user-consent';
import { logger } from '@rhg/libs/logger';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import DoubleOptInConfirmDialog from '../dialogs/double-opt-in-confirm';

interface EmailOptInConfirmationProps {
    confirmationCode: string;
    onClose?: () => void;
}

export default function EmailOptInConfirmation({
    confirmationCode,
    onClose,
}: EmailOptInConfirmationProps) {
    const { push } = useRouter();
    const { account } = useAccount();
    const { notificationConsentUpdateAsync } =
        useNotificationUserConsentUpdate();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);
    const hasConfirmedRef = useRef(false);

    useEffect(() => {
        const runStatusUpdate = async () => {
            try {
                await notificationConsentUpdateAsync({
                    code: confirmationCode,
                });
            } catch (error) {
                setError(true);
                logger.error(
                    'Error from submitting invitation step',
                    {},
                    error as Error
                );
            } finally {
                setIsLoading(false);
            }
        };

        if (confirmationCode && account && !hasConfirmedRef.current) {
            runStatusUpdate().catch((err) =>
                logger.error('Failed to run status update', {}, err as Error)
            );
        }
    }, [confirmationCode, account, notificationConsentUpdateAsync]);

    if (isLoading) {
        return <FullWidthLinearProgress />;
    }

    if (error) {
        const handleErrorClose = () => {
            push(routes.dashboard).catch((err) =>
                logger.error(
                    'Failed to navigate to dashboard',
                    {},
                    err as Error
                )
            );
        };
        return (
            <GeneralErrorDialog
                onClose={handleErrorClose}
                showSupportEmail={true}
                showSupportMessage={true}
            />
        );
    }

    return <DoubleOptInConfirmDialog isDialogOpen={!error} onClose={onClose} />;
}
